import { Anchor } from "@excel/anchor";
import type { Cell } from "@excel/cell";
import { Column } from "@excel/column";
import { DataValidations } from "@excel/data-validations";
import {
  ExcelStreamStateError,
  ImageError,
  MergeConflictError,
  RowOutOfBoundsError
} from "@excel/errors";
import { Dimensions } from "@excel/range";
import { Row } from "@excel/row";
import { SheetCommentsWriter } from "@excel/stream/sheet-comments-writer";
import { SheetRelsWriter } from "@excel/stream/sheet-rels-writer";
import type { Medium as WriterMedium } from "@excel/stream/workbook-writer";
import { colCache } from "@excel/utils/col-cache";
import {
  buildDrawingAnchorsAndRels,
  isExternalImage,
  type DrawingAnchor,
  type DrawingRel
} from "@excel/utils/drawing-utils";
import { applyMergeBorders, collectMergeBorders } from "@excel/utils/merge-borders";
import {
  drawingRelTargetFromWorksheet,
  mediaRelTargetFromRels,
  worksheetPath
} from "@excel/utils/ooxml-paths";
import type { SharedStrings } from "@excel/utils/shared-strings";
import { buildSheetProtection } from "@excel/utils/sheet-protection";
import type { StreamBuf } from "@excel/utils/stream-buf";
import { StringBuf } from "@excel/utils/string-buf";
import { RelType } from "@excel/xlsx/rel-type";

const xmlBuffer = /* @__PURE__ */ new StringBuf();

import type {
  RowBreak,
  ColBreak,
  PageSetup,
  HeaderFooter,
  WorksheetProperties,
  WorksheetView,
  WorksheetState,
  AutoFilter,
  WorksheetProtection,
  ConditionalFormattingOptions,
  AddImageRange,
  IgnoredError,
  WatermarkOptions,
  RowValues
} from "@excel/types";
// ============================================================================================
// Xforms
import { ListXform } from "@excel/xlsx/xform/list-xform";
import { AutoFilterXform } from "@excel/xlsx/xform/sheet/auto-filter-xform";
import { ConditionalFormattingsXform } from "@excel/xlsx/xform/sheet/cf/conditional-formattings-xform";
import { ColBreaksXform } from "@excel/xlsx/xform/sheet/col-breaks-xform";
import { ColXform } from "@excel/xlsx/xform/sheet/col-xform";
import { DataValidationsXform } from "@excel/xlsx/xform/sheet/data-validations-xform";
import { DrawingXform as DrawingPartXform } from "@excel/xlsx/xform/sheet/drawing-xform";
import { ExtLstXform } from "@excel/xlsx/xform/sheet/ext-lst-xform";
import { HeaderFooterXform } from "@excel/xlsx/xform/sheet/header-footer-xform";
import { HyperlinkXform } from "@excel/xlsx/xform/sheet/hyperlink-xform";
import { IgnoredErrorsXform } from "@excel/xlsx/xform/sheet/ignored-errors-xform";
import { PageMarginsXform } from "@excel/xlsx/xform/sheet/page-margins-xform";
import { PageSetupXform } from "@excel/xlsx/xform/sheet/page-setup-xform";
import { PictureXform } from "@excel/xlsx/xform/sheet/picture-xform";
import { RowBreaksXform } from "@excel/xlsx/xform/sheet/row-breaks-xform";
import { RowXform } from "@excel/xlsx/xform/sheet/row-xform";
import { SheetFormatPropertiesXform } from "@excel/xlsx/xform/sheet/sheet-format-properties-xform";
import { SheetPropertiesXform } from "@excel/xlsx/xform/sheet/sheet-properties-xform";
import { SheetProtectionXform } from "@excel/xlsx/xform/sheet/sheet-protection-xform";
import { SheetViewXform } from "@excel/xlsx/xform/sheet/sheet-view-xform";

// since prepare and render are functional, we can use singletons
const xform = {
  dataValidations: new DataValidationsXform(),
  sheetProperties: new SheetPropertiesXform(),
  sheetFormatProperties: new SheetFormatPropertiesXform(),
  columns: new ListXform({ tag: "cols", count: false, childXform: new ColXform() } as any),
  row: new RowXform(),
  hyperlinks: new ListXform({
    tag: "hyperlinks",
    count: false,
    childXform: new HyperlinkXform()
  } as any),
  sheetViews: new ListXform({
    tag: "sheetViews",
    count: false,
    childXform: new SheetViewXform()
  } as any),
  sheetProtection: new SheetProtectionXform(),
  pageMargins: new PageMarginsXform(),
  pageSeteup: new PageSetupXform(),
  autoFilter: new AutoFilterXform(),
  picture: new PictureXform(),
  drawing: new DrawingPartXform(),
  conditionalFormattings: new ConditionalFormattingsXform(),
  extLst: new ExtLstXform(),
  ignoredErrors: new IgnoredErrorsXform(),
  headerFooter: new HeaderFooterXform(),
  rowBreaks: new RowBreaksXform(),
  colBreaks: new ColBreaksXform()
};

// ============================================================================================

/**
 * Structural view of the fields/methods WorksheetWriter needs from its
 * parent WorkbookWriter. Defined here (rather than importing WorkbookWriter
 * directly) to avoid the circular `workbook-writer.ts <-> worksheet-writer.ts`
 * dependency. The shape must stay in sync with the concrete WorkbookWriter.
 */
export interface WorkbookWriterLike {
  /** Shared-string table (deduplicates plain/rich-text cell values). */
  readonly sharedStrings: SharedStrings;
  /**
   * Style manager. Typed loosely here (`unknown`) because the concrete
   * `StylesXform` is an internal xform class and pulling it in would
   * reintroduce the circular import. Xform methods accept it by duck-typing.
   */
  readonly styles: object;
  /** Incremented once per dynamic-array formula cell during row commit. */
  dynamicArrayCount: number;
  /** Lookup a media (image/chart) by registered id. */
  getImage(id: number): WriterMedium | undefined;
  /** Open a streaming entry in the output zip for the given path. */
  _openStream(path: string): InstanceType<typeof StreamBuf>;
}

interface WorksheetWriterOptions {
  id: number;
  name?: string;
  workbook: WorkbookWriterLike;
  useSharedStrings?: boolean;
  properties?: Partial<WorksheetProperties>;
  state?: WorksheetState;
  pageSetup?: Partial<PageSetup>;
  views?: Partial<WorksheetView>[];
  autoFilter?: AutoFilter;
  headerFooter?: Partial<HeaderFooter>;
}

/** Internal model for an image added via addImage(). */
interface WriterImageModel {
  type: "image";
  imageId: string;
  range: {
    tl: { nativeCol: number; nativeColOff: number; nativeRow: number; nativeRowOff: number };
    br?: { nativeCol: number; nativeColOff: number; nativeRow: number; nativeRowOff: number };
    ext?: { width: number; height: number };
    editAs?: string;
    /** Absolute position in pixels — mutually exclusive with tl/br cell anchors. */
    pos?: { x: number; y: number };
  };
  hyperlinks?: { hyperlink?: string; tooltip?: string };
}

class WorksheetWriter {
  id: number;
  name: string;
  state: WorksheetState;
  /** Rows stored while being worked on. Set to null after commit. */
  private _rows: Row[] | null;
  /** Column definitions */
  private _columns: Column[];
  /** Column keys mapping: key => Column */
  private _keys: { [key: string]: Column };
  /** Merged cell ranges */
  private _merges: Dimensions[];
  private _sheetRelsWriter: SheetRelsWriter;
  private _sheetCommentsWriter: SheetCommentsWriter;
  private _dimensions: Dimensions;
  private _rowZero: number;
  private _rowOffset: number;
  committed: boolean;
  dataValidations: DataValidations;
  /** Shared formulae by address */
  private _formulae: { [key: string]: unknown };
  private _siFormulae: number;
  conditionalFormatting: ConditionalFormattingOptions[];
  ignoredErrors: IgnoredError[];
  rowBreaks: RowBreak[];
  colBreaks: ColBreak[];
  properties: Partial<WorksheetProperties> & {
    defaultRowHeight: number;
    dyDescent?: number;
    outlineLevelCol: number;
    outlineLevelRow: number;
  };
  headerFooter: Partial<HeaderFooter>;
  pageSetup: Partial<PageSetup> & { margins: PageSetup["margins"] };
  useSharedStrings: boolean;
  // WorkbookWriter parent — structural reference to avoid the circular
  // dependency between workbook-writer.ts and worksheet-writer.ts.
  private _workbook: WorkbookWriterLike;
  hasComments: boolean;
  private _views: Partial<WorksheetView>[];
  autoFilter: AutoFilter | null;
  private _media: WriterImageModel[];
  sheetProtection: {
    sheet?: boolean;
    algorithmName?: string;
    saltValue?: string;
    spinCount?: number;
    hashValue?: string;
    [key: string]: unknown;
  } | null;
  private _stream?: InstanceType<typeof StreamBuf>;
  startedData: boolean;
  private _background?: { imageId?: number; rId?: string };
  private _headerRowCount?: number;
  /** Watermark configuration */
  private _watermark: WatermarkOptions | null;
  /** Drawing model — populated during commit if images were added */
  private _drawing?: {
    rId: string;
    name: string;
    anchors: DrawingAnchor[];
    rels: DrawingRel[];
  };
  /** Relationship Id - assigned by WorkbookWriter */
  rId?: string;

  constructor(options: WorksheetWriterOptions) {
    // in a workbook, each sheet will have a number
    this.id = options.id;

    // and a name
    this.name = options.name || `Sheet${this.id}`;

    // add a state
    this.state = options.state ?? "visible";

    // rows are stored here while they need to be worked on.
    // when they are committed, they will be deleted.
    this._rows = [];

    // column definitions
    this._columns = [];

    // column keys (addRow convenience): key ==> this._columns index
    this._keys = {};

    // keep a record of all row and column pageBreaks
    this._merges = [];
    (this._merges as any).add = function () {}; // ignore cell instruction

    // keep record of all hyperlinks
    this._sheetRelsWriter = new SheetRelsWriter(options);

    this._sheetCommentsWriter = new SheetCommentsWriter(this, this._sheetRelsWriter, options);

    // keep a record of dimensions
    this._dimensions = new Dimensions();

    // first uncommitted row
    this._rowZero = 1;

    // Internal offset into `_rows` to avoid O(n) `Array.shift()` in hot path.
    this._rowOffset = 0;

    // committed flag
    this.committed = false;

    // for data validations
    this.dataValidations = new DataValidations();

    // for sharing formulae
    this._formulae = {};
    this._siFormulae = 0;

    // keep a record of conditionalFormattings
    this.conditionalFormatting = [];

    // ignored errors (suppress green triangles in Excel)
    this.ignoredErrors = [];

    // keep a record of all row and column pageBreaks
    this.rowBreaks = [];
    this.colBreaks = [];

    // for default row height, outline levels, etc
    this.properties = Object.assign(
      {},
      {
        defaultRowHeight: 15,
        outlineLevelCol: 0,
        outlineLevelRow: 0
      },
      options.properties
    );

    this.headerFooter = Object.assign(
      {},
      {
        differentFirst: false,
        differentOddEven: false,
        oddHeader: null,
        oddFooter: null,
        evenHeader: null,
        evenFooter: null,
        firstHeader: null,
        firstFooter: null
      },
      options.headerFooter
    );

    // for all things printing
    this.pageSetup = Object.assign(
      {},
      {
        margins: { left: 0.7, right: 0.7, top: 0.75, bottom: 0.75, header: 0.3, footer: 0.3 },
        orientation: "portrait",
        horizontalDpi: 4294967295,
        verticalDpi: 4294967295,
        fitToPage: !!(
          options.pageSetup &&
          (options.pageSetup.fitToWidth || options.pageSetup.fitToHeight) &&
          !options.pageSetup.scale
        ),
        pageOrder: "downThenOver",
        blackAndWhite: false,
        draft: false,
        cellComments: "None",
        errors: "displayed",
        scale: 100,
        fitToWidth: 1,
        fitToHeight: 1,
        paperSize: undefined,
        showRowColHeaders: false,
        showGridLines: false,
        horizontalCentered: false,
        verticalCentered: false,
        rowBreaks: null,
        colBreaks: null
      },
      options.pageSetup
    );

    // using shared strings creates a smaller xlsx file but may use more memory
    this.useSharedStrings = options.useSharedStrings ?? false;

    this._workbook = options.workbook;

    this.hasComments = false;

    // views
    this._views = options.views ?? [];

    // auto filter
    this.autoFilter = options.autoFilter ?? null;

    this._media = [];

    // watermark
    this._watermark = null;

    // worksheet protection
    this.sheetProtection = null;

    // start writing to stream now
    this._writeOpenWorksheet();

    this.startedData = false;
  }

  get workbook(): WorkbookWriterLike {
    return this._workbook;
  }

  get stream(): InstanceType<typeof StreamBuf> {
    if (!this._stream) {
      this._stream = this._workbook._openStream(worksheetPath(this.id));

      // DO NOT pause stream - fflate migration requires data events to flow
      // The stream uses 'data' events to pipe to ZipPassThrough
      // this._stream.pause();
    }
    return this._stream;
  }

  // destroy - not a valid operation for a streaming writer
  // even though some streamers might be able to, it's a bad idea.
  destroy(): void {
    throw new ExcelStreamStateError("destroy", "Invalid operation for a streaming writer");
  }

  commit(): void {
    if (this.committed) {
      return;
    }
    // commit all rows
    for (let i = this._rowOffset; i < this._rows!.length; i++) {
      const cRow = this._rows![i];
      if (cRow) {
        this._writeRow(cRow);
      }
    }

    // we _cannot_ accept new rows from now on
    this._rows = null;

    if (!this.startedData) {
      this._writeOpenSheetData();
    }
    this._writeCloseSheetData();
    this._writeSheetProtection(); // Note: must be after sheetData and before autoFilter
    this._writeAutoFilter();
    this._writeMergeCells();

    // for some reason, Excel can't handle dimensions at the bottom of the file
    // this._writeDimensions();

    this._writeHyperlinks();
    this._writeConditionalFormatting();
    this._writeDataValidations();
    this._writePageMargins();
    this._writePageSetup();
    this._writeHeaderFooter();
    this._writeRowBreaks();
    this._writeColBreaks();
    this._writeDrawing(); // Note: must be after rowBreaks/colBreaks
    this._writeBackground(); // Note: must be after drawing

    // Legacy Data tag for comments
    this._writeLegacyData();

    // ignoredErrors must be before extLst
    this._writeIgnoredErrors();

    // extLst must be the last child element before </worksheet>
    this._writeExtLst();

    this._writeCloseWorksheet();
    // signal end of stream to workbook
    this.stream.end();

    this._sheetCommentsWriter.commit();
    // also commit the hyperlinks if any
    this._sheetRelsWriter.commit();

    this.committed = true;
  }

  // return the current dimensions of the writer
  get dimensions(): Dimensions {
    return this._dimensions;
  }

  get views(): Partial<WorksheetView>[] {
    return this._views;
  }

  // =========================================================================
  // Columns

  // get the current columns array.
  get columns(): Column[] {
    return this._columns;
  }

  // set the columns from an array of column definitions.
  // Note: any headers defined will overwrite existing values.
  set columns(value: Partial<Column>[]) {
    // calculate max header row count
    this._headerRowCount = value.reduce((pv, cv) => {
      const headerCount = (cv.header && 1) || (cv.headers && cv.headers.length) || 0;
      return Math.max(pv, headerCount);
    }, 0);

    // construct Column objects
    let count = 1;
    const columns: Column[] = (this._columns = []);
    value.forEach(defn => {
      const column = new Column(this as any, count++, false as any);
      columns.push(column);
      column.defn = defn;
    });
  }

  getColumnKey(key: string): Column | undefined {
    return this._keys[key];
  }

  setColumnKey(key: string, value: Column): void {
    this._keys[key] = value;
  }

  deleteColumnKey(key: string): void {
    delete this._keys[key];
  }

  eachColumnKey(f: (column: Column, key: string) => void): void {
    Object.keys(this._keys).forEach(key => f(this._keys[key], key));
  }

  // get a single column by col number. If it doesn't exist, it and any gaps before it
  // are created.
  getColumn(c: string | number): Column {
    if (typeof c === "string") {
      // if it matches a key'd column, return that
      const col = this._keys[c];
      if (col) {
        return col;
      }

      // otherwise, assume letter
      c = colCache.l2n(c);
    }
    if (c > this._columns.length) {
      let n = this._columns.length + 1;
      while (n <= c) {
        this._columns.push(new Column(this as any, n++));
      }
    }
    return this._columns[c - 1];
  }

  // =========================================================================
  // Rows
  private get _nextRow(): number {
    return this._rowZero + (this._rows!.length - this._rowOffset);
  }

  // iterate over every uncommitted row in the worksheet, including maybe empty rows
  eachRow(
    options: { includeEmpty?: boolean } | ((row: Row, rowNumber: number) => void),
    iteratee?: (row: Row, rowNumber: number) => void
  ): void {
    let callback: ((row: Row, rowNumber: number) => void) | undefined;
    let opts: { includeEmpty?: boolean } | undefined;

    if (typeof options === "function") {
      callback = options;
      opts = undefined;
    } else {
      callback = iteratee;
      opts = options;
    }

    if (opts && opts.includeEmpty) {
      const n = this._nextRow;
      for (let i = this._rowZero; i < n; i++) {
        callback!(this.getRow(i), i);
      }
    } else {
      this._rows!.forEach(row => {
        if (row && row.hasValues) {
          callback!(row, row.number);
        }
      });
    }
  }

  private _commitRow(cRow: Row): void {
    // since rows must be written in order, we commit all rows up till and including cRow
    let found = false;

    const rows = this._rows!;
    while (this._rowOffset < rows.length && !found) {
      const row = rows[this._rowOffset];
      rows[this._rowOffset] = undefined as any;
      this._rowOffset++;
      this._rowZero++;
      if (row) {
        this._writeRow(row);
        found = row.number === cRow.number;
        this._rowZero = row.number + 1;
      }

      // Occasionally compact the buffer to keep indices small and reduce memory.
      if (this._rowOffset > 1024 && this._rowOffset > rows.length >> 1) {
        rows.splice(0, this._rowOffset);
        this._rowOffset = 0;
      }
    }
  }

  get lastRow(): Row | undefined {
    // returns last uncommitted row
    const rows = this._rows!;
    for (let i = rows.length - 1; i >= this._rowOffset; i--) {
      const row = rows[i];
      if (row) {
        return row;
      }
    }
    return undefined;
  }

  // find a row (if exists) by row number
  findRow(rowNumber: number): Row | undefined {
    const index = rowNumber - this._rowZero + this._rowOffset;
    return this._rows![index];
  }

  getRow(rowNumber: number): Row {
    const index = rowNumber - this._rowZero + this._rowOffset;

    // may fail if rows have been comitted
    if (index < this._rowOffset) {
      throw new RowOutOfBoundsError(rowNumber, "this row has been committed");
    }
    let row = this._rows![index];
    if (!row) {
      this._rows![index] = row = new Row(this as any, rowNumber);
    }
    return row;
  }

  addRow(value: RowValues): Row {
    const row = new Row(this as any, this._nextRow);
    this._rows![row.number - this._rowZero + this._rowOffset] = row;
    row.values = value;
    return row;
  }

  addRows(values: RowValues[]): Row[] {
    return values.map(value => this.addRow(value));
  }

  // ================================================================================
  // Cells

  // returns the cell at [r,c] or address given by r. If not found, return undefined
  findCell(r: string | number, c?: number): Cell | undefined {
    const address = colCache.getAddress(r, c);
    const row = this.findRow(address.row);
    return row ? row.findCell(address.col) : undefined;
  }

  // return the cell at [r,c] or address given by r. If not found, create a new one.
  getCell(r: string | number, c?: number): Cell {
    const address = colCache.getAddress(r, c);
    const row = this.getRow(address.row);
    return row.getCellEx(address);
  }

  mergeCells(...cells: (string | number)[]): void {
    // may fail if rows have been comitted
    const dimensions = new Dimensions(cells);

    // check cells aren't already merged
    this._merges.forEach(merge => {
      if (merge.intersects(dimensions)) {
        throw new MergeConflictError();
      }
    });

    const { top, left, bottom, right } = dimensions;

    // Collect perimeter borders BEFORE merge overwrites slave styles
    const collected = collectMergeBorders(top, left, bottom, right, (r, c) => this.findCell(r, c));

    // Apply merge
    const master = this.getCell(top, left);
    for (let i = top; i <= bottom; i++) {
      for (let j = left; j <= right; j++) {
        if (i > top || j > left) {
          this.getCell(i, j).merge(master);
        }
      }
    }

    // Reconstruct position-aware borders (like Excel)
    if (collected) {
      applyMergeBorders(top, left, bottom, right, collected, (r, c) => this.getCell(r, c));
    }

    // index merge
    this._merges.push(dimensions);
  }

  // ===========================================================================
  // Conditional Formatting
  addConditionalFormatting(cf: ConditionalFormattingOptions): void {
    this.conditionalFormatting.push(cf);
  }

  removeConditionalFormatting(
    filter?: number | ((cf: ConditionalFormattingOptions) => boolean)
  ): void {
    if (typeof filter === "number") {
      this.conditionalFormatting.splice(filter, 1);
    } else if (typeof filter === "function") {
      // Predicate selects rules to drop, not rules to keep.
      this.conditionalFormatting = this.conditionalFormatting.filter(cf => !filter(cf));
    } else {
      this.conditionalFormatting = [];
    }
  }

  // =========================================================================

  addBackgroundImage(imageId: string | number): void {
    const bookImage = this._workbook.getImage(Number(imageId));
    if (bookImage && isExternalImage(bookImage)) {
      throw new ImageError(
        "Background images cannot be external (linked) images. " +
          "Use an embedded image (buffer/base64/filename). " +
          "External images are only supported for cell pictures and overlay watermarks."
      );
    }
    this._background = {
      imageId: Number(imageId)
    };
  }

  getBackgroundImageId(): number | undefined {
    return this._background && this._background.imageId;
  }

  // =========================================================================
  // Images

  /**
   * Using the image id from `WorkbookWriter.addImage`,
   * embed an image within the worksheet to cover a range.
   */
  addImage(imageId: string | number, range: AddImageRange): void {
    const model = this._parseImageRange(String(imageId), range);
    this._media.push(model);
  }

  /**
   * Return the images that have been added to this worksheet.
   * Each entry contains imageId and the normalised range (with native anchors).
   */
  getImages(): ReadonlyArray<WriterImageModel> {
    return this._media;
  }

  // =========================================================================
  // Watermark

  /**
   * Add a watermark to the worksheet using an image from `WorkbookWriter.addImage()`.
   * Supports overlay mode (DrawingML with transparency) and header mode (VML behind content).
   *
   * `mode: "overlay"` supports external (linked) images; `mode: "header"` does
   * not — VML header/footer images require embedded media, so a linked image
   * with `mode: "header"` throws an `ImageError`.
   *
   * @throws {ImageError} If `mode: "header"` is used with an external (linked) image.
   */
  addWatermark(options: WatermarkOptions): void {
    const mode = options.mode ?? "overlay";

    // Validate BEFORE mutating any state: VML header/footer images use
    // embedded media; external (linked) images are not representable here.
    // Reject them up front so a failed call leaves existing watermark media
    // untouched (no partial mutation).
    if (mode === "header") {
      const bookImage = this._workbook.getImage(Number(options.imageId));
      if (bookImage && isExternalImage(bookImage)) {
        throw new ImageError(
          "Header watermark images cannot be external (linked) images. " +
            "Use an embedded image (buffer/base64/filename), or use overlay mode for linked images."
        );
      }
    }

    // Remove existing watermark entries (both stored type tags)
    this._media = this._media.filter(m => (m as any)._watermarkTag !== true);

    const opacity =
      options.opacity !== undefined ? Math.max(0, Math.min(1, options.opacity)) : 0.15;

    this._watermark = {
      imageId: String(options.imageId),
      mode,
      opacity,
      headerWidth: options.headerWidth,
      headerHeight: options.headerHeight,
      applyTo: options.applyTo
    };

    if (this._watermark.mode === "overlay") {
      // Coverage range is computed lazily during commit() via _resolveWatermarkRange()
      const entry = {
        type: "image",
        imageId: String(options.imageId),
        range: {
          tl: { nativeCol: 0, nativeColOff: 0, nativeRow: 0, nativeRowOff: 0 },
          br: { nativeCol: 100, nativeColOff: 0, nativeRow: 200, nativeRowOff: 0 },
          editAs: "absolute"
        },
        // Internal tag for dedup — not part of the WriterImageModel type
        _watermarkTag: true,
        opacity
      };
      this._media.push(entry as any);
    }
    // Note: header mode for streaming writer is limited — the VML file generation
    // happens in WorkbookWriter.addWorksheets(), which handles worksheet.headerImage.
    // We store the config in _watermark and it's picked up by the commit path.
  }

  /**
   * Get the current watermark configuration.
   */
  getWatermark(): WatermarkOptions | null {
    return this._watermark;
  }

  /**
   * Remove the watermark from the worksheet.
   */
  removeWatermark(): void {
    this._watermark = null;
    this._media = this._media.filter(m => (m as any)._watermarkTag !== true);
  }

  /**
   * Parse the user-supplied range into a normalised internal model
   * mirroring what the regular Worksheet / Image class does.
   */
  private _parseImageRange(imageId: string, range: AddImageRange): WriterImageModel {
    if (typeof range === "string") {
      // e.g. "A1:C3"
      const decoded = colCache.decode(range);
      if ("top" in decoded) {
        return {
          type: "image",
          imageId,
          range: {
            tl: new Anchor(this as any, { col: decoded.left, row: decoded.top }, -1).model,
            br: new Anchor(this as any, { col: decoded.right, row: decoded.bottom }, 0).model,
            editAs: "oneCell"
          }
        };
      }
      throw new Error(`Invalid image range: "${range}". Expected a range like "A1:C3".`);
    }

    // Absolute positioning (pos + ext, no cell anchors)
    if ("pos" in range && range.pos) {
      return {
        type: "image",
        imageId,
        range: {
          tl: { nativeCol: 0, nativeColOff: 0, nativeRow: 0, nativeRowOff: 0 },
          ext: range.ext,
          pos: range.pos
        },
        hyperlinks: range.hyperlinks
      };
    }

    // Cell-based positioning (tl/br anchors)
    const cellRange = range as Exclude<typeof range, string | { pos: any }>;
    const tl = new Anchor(this as any, cellRange.tl as any, 0).model;
    const br = cellRange.br ? new Anchor(this as any, cellRange.br as any, 0).model : undefined;
    return {
      type: "image",
      imageId,
      range: {
        tl,
        br,
        ext: cellRange.ext,
        editAs: cellRange.editAs
      },
      hyperlinks: cellRange.hyperlinks
    };
  }

  // =========================================================================
  // Worksheet Protection
  async protect(password?: string, options?: Partial<WorksheetProtection>): Promise<void> {
    // Synchronous pre-set so callers that don't await still see sheetProtection
    // before commit() (the original code was synchronous on the no-password path).
    this.sheetProtection = { sheet: true };
    this.sheetProtection = await buildSheetProtection(password, options);
  }

  unprotect(): void {
    this.sheetProtection = null;
  }

  // ================================================================================

  private _write(text: string): void {
    xmlBuffer.reset();
    xmlBuffer.addText(text);
    this.stream.write(xmlBuffer);
  }

  private _writeSheetProperties(
    xmlBuf: StringBuf,
    properties: Partial<WorksheetProperties> | undefined,
    pageSetup: Partial<PageSetup> | undefined
  ): void {
    const sheetPropertiesModel = {
      outlineProperties: properties && properties.outlineProperties,
      tabColor: properties && properties.tabColor,
      pageSetup:
        pageSetup && pageSetup.fitToPage
          ? {
              fitToPage: pageSetup.fitToPage
            }
          : undefined
    };

    xmlBuf.addText(xform.sheetProperties.toXml(sheetPropertiesModel));
  }

  private _writeSheetFormatProperties(
    xmlBuf: StringBuf,
    properties: Partial<WorksheetProperties> | undefined
  ): void {
    const sheetFormatPropertiesModel = properties
      ? {
          defaultRowHeight: properties.defaultRowHeight,
          dyDescent: properties.dyDescent,
          outlineLevelCol: properties.outlineLevelCol,
          outlineLevelRow: properties.outlineLevelRow
        }
      : undefined;
    if (properties && properties.defaultColWidth) {
      (sheetFormatPropertiesModel as any).defaultColWidth = properties.defaultColWidth;
    }

    xmlBuf.addText(xform.sheetFormatProperties.toXml(sheetFormatPropertiesModel));
  }

  private _writeOpenWorksheet(): void {
    xmlBuffer.reset();

    xmlBuffer.addText('<?xml version="1.0" encoding="UTF-8" standalone="yes"?>');
    xmlBuffer.addText(
      '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"' +
        ' xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"' +
        ' xmlns:mc="http://schemas.openxmlformats.org/markup-compatibility/2006"' +
        ' mc:Ignorable="x14ac"' +
        ' xmlns:x14ac="http://schemas.microsoft.com/office/spreadsheetml/2009/9/ac">'
    );

    this._writeSheetProperties(xmlBuffer, this.properties, this.pageSetup);

    xmlBuffer.addText(xform.sheetViews.toXml(this.views));

    this._writeSheetFormatProperties(xmlBuffer, this.properties);

    this.stream.write(xmlBuffer);
  }

  private _writeColumns(): void {
    const cols = (Column as any).toModel(this.columns);
    if (cols) {
      xform.columns.prepare(cols, { styles: this._workbook.styles });
      this.stream.write(xform.columns.toXml(cols));
    }
  }

  private _writeOpenSheetData(): void {
    this._write("<sheetData>");
  }

  private _writeRow(row: Row): void {
    if (!this.startedData) {
      this._writeColumns();
      this._writeOpenSheetData();
      this.startedData = true;
    }

    if (row.hasValues || row.height != null) {
      const { model } = row;
      if (!model) {
        return;
      }
      const options = {
        styles: this._workbook.styles,
        sharedStrings: this.useSharedStrings ? this._workbook.sharedStrings : undefined,
        hyperlinks: this._sheetRelsWriter.hyperlinksProxy,
        merges: this._merges,
        formulae: this._formulae,
        siFormulae: this._siFormulae,
        comments: []
      };
      xform.row.prepare(model, options);
      this.stream.write(xform.row.toXml(model));

      // Count dynamic array formula cells for metadata generation
      if (model.cells) {
        for (const cell of model.cells) {
          if (cell && cell.isDynamicArray) {
            this._workbook.dynamicArrayCount++;
          }
        }
      }

      if (options.comments.length) {
        this.hasComments = true;
        this._sheetCommentsWriter.addComments(options.comments);
      }
    }
  }

  private _writeCloseSheetData(): void {
    this._write("</sheetData>");
  }

  private _writeMergeCells(): void {
    if (this._merges.length) {
      xmlBuffer.reset();
      xmlBuffer.addText(`<mergeCells count="${this._merges.length}">`);
      this._merges.forEach(merge => {
        xmlBuffer.addText(`<mergeCell ref="${merge}"/>`);
      });
      xmlBuffer.addText("</mergeCells>");

      this.stream.write(xmlBuffer);
    }
  }

  private _writeHyperlinks(): void {
    this.stream.write(xform.hyperlinks.toXml(this._sheetRelsWriter._hyperlinks));
  }

  private _writeConditionalFormatting(): void {
    const options = {
      styles: this._workbook.styles
    };

    // Prepare both primary and ext sections upfront.
    // The primary prepare handles priorities, dxfId, and dataBar defaults.
    // The ext prepare (via ExtLstXform) assigns x14Id for rules that need
    // an ext section (dataBar, custom iconSet). Both must run before either
    // section is rendered, because the primary <cfRule> references the ext
    // via <x14:id>. This mirrors the non-streaming path where
    // WorkSheetXform.prepare() calls both chains sequentially.
    xform.conditionalFormattings.prepare(this.conditionalFormatting, options);
    const extModel = { conditionalFormattings: this.conditionalFormatting };
    xform.extLst.prepare(extModel);

    // Render primary section (position: after hyperlinks, before dataValidations)
    this.stream.write(xform.conditionalFormattings.toXml(this.conditionalFormatting));
  }

  /**
   * Write the `<extLst>` section at the end of the worksheet.
   * Currently this only contains conditional formatting extensions (data bar
   * ext attributes, custom icon sets), but the ExtLstXform will automatically
   * skip rendering when there is no ext content.
   *
   * The prepare phase was already done in _writeConditionalFormatting().
   */
  private _writeExtLst(): void {
    const model = { conditionalFormattings: this.conditionalFormatting };
    this.stream.write(xform.extLst.toXml(model));
  }

  private _writeIgnoredErrors(): void {
    if (this.ignoredErrors.length > 0) {
      this.stream.write(xform.ignoredErrors.toXml(this.ignoredErrors));
    }
  }

  private _writeRowBreaks(): void {
    this.stream.write(xform.rowBreaks.toXml(this.rowBreaks));
  }

  private _writeColBreaks(): void {
    this.stream.write(xform.colBreaks.toXml(this.colBreaks));
  }

  private _writeDataValidations(): void {
    this.stream.write(xform.dataValidations.toXml(this.dataValidations.model));
  }

  private _writeSheetProtection(): void {
    this.stream.write(xform.sheetProtection.toXml(this.sheetProtection));
  }

  private _writePageMargins(): void {
    this.stream.write(xform.pageMargins.toXml(this.pageSetup.margins));
  }

  private _writePageSetup(): void {
    this.stream.write(xform.pageSeteup.toXml(this.pageSetup));
  }

  private _writeHeaderFooter(): void {
    this.stream.write(xform.headerFooter.toXml(this.headerFooter));
  }

  private _writeAutoFilter(): void {
    this.stream.write(xform.autoFilter.toXml(this.autoFilter));
  }

  private _writeDrawing(): void {
    if (this._media.length === 0) {
      return;
    }

    // Resolve watermark coverage range from actual worksheet dimensions
    // (at commit time, all rows have been flushed so _dimensions is accurate)
    for (const entry of this._media) {
      if ((entry as any)._watermarkTag) {
        const dims = this._dimensions.model;
        const maxCol = dims ? Math.max(dims.right ?? 100, 100) : 100;
        const maxRow = dims ? Math.max(dims.bottom ?? 200, 200) : 200;
        (entry as any).range.br = {
          nativeCol: maxCol,
          nativeColOff: 0,
          nativeRow: maxRow,
          nativeRowOff: 0
        };
      }
    }

    // Build the drawing model from the stored images.
    // The drawing XML will be generated later by WorkbookWriterBase.addDrawings().
    const drawingName = `drawing${this.id}`;
    const drawingRId = this._sheetRelsWriter.addRelationship({
      Type: "http://schemas.openxmlformats.org/officeDocument/2006/relationships/drawing",
      Target: drawingRelTargetFromWorksheet(drawingName)
    });

    // Build anchors and drawing-level rels using the shared utility
    const { anchors, rels } = buildDrawingAnchorsAndRels(this._media, [], {
      getBookImage: id => this._workbook.getImage(Number(id)),
      nextRId: currentRels => `rId${currentRels.length + 1}`
    });

    // Store drawing model for the workbook writer to generate the actual drawing XML
    this._drawing = {
      rId: drawingRId,
      name: drawingName,
      anchors,
      rels
    };

    // Write <drawing r:id="rIdN"/> into the worksheet XML
    this.stream.write(xform.drawing.toXml({ rId: drawingRId }));
  }

  /** Returns the drawing model if images were added, for the workbook writer. */
  get drawing():
    | { rId: string; name: string; anchors: DrawingAnchor[]; rels: DrawingRel[] }
    | undefined {
    return this._drawing;
  }

  private _writeBackground(): void {
    if (this._background) {
      if (this._background.imageId !== undefined) {
        const image = this._workbook.getImage(this._background.imageId);
        if (!image) {
          return;
        }
        // Background images are always embedded — external (linked) images are
        // rejected up front in addBackgroundImage (Excel drops them).
        const pictureId = this._sheetRelsWriter.addMedia({
          Target: mediaRelTargetFromRels(image.name),
          Type: RelType.Image
        });

        this._background = {
          ...this._background,
          rId: pictureId
        };
      }
      this.stream.write(xform.picture.toXml({ rId: this._background.rId }));
    }
  }

  private _writeLegacyData(): void {
    if (this.hasComments) {
      xmlBuffer.reset();
      xmlBuffer.addText(`<legacyDrawing r:id="${this._sheetCommentsWriter.vmlRelId}"/>`);
      this.stream.write(xmlBuffer);
    }
  }

  private _writeDimensions(): void {
    // for some reason, Excel can't handle dimensions at the bottom of the file
    // and we don't know the dimensions until the commit, so don't write them.
    // this._write('<dimension ref="' + this._dimensions + '"/>');
  }

  private _writeCloseWorksheet(): void {
    this._write("</worksheet>");
  }
}

export { WorksheetWriter };
export type { WriterImageModel };
